import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/layout/Layout';
import { PageSeoHead } from '@/components/seo/PageSeoHead';
import { StructuredData } from '@/components/seo/StructuredData';
import { ArticleCard } from '@/components/blog/ArticleCard';
import { useBlogArticles } from '@/hooks/useBlogArticles';
import { Skeleton } from '@/components/ui/skeleton';

export default function Blog() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'it';

  const { data: articles, isLoading, isError } = useBlogArticles(50);

  const title = lang === 'it'
    ? 'Blog — Gioielli Caccia, Montagna e Fauna | Sampaoli Creazioni'
    : 'Blog — Hunting, Mountain & Wildlife Jewelry | Sampaoli Creazioni';
  const description = lang === 'it'
    ? 'Articoli su gioielli artigianali, cultura venatoria, fauna alpina e montagna. Scopri il significato dietro ogni creazione Sampaoli.'
    : 'Articles on artisan jewelry, hunting culture, alpine wildlife, and mountains. Discover the meaning behind every Sampaoli creation.';

  return (
    <Layout>
      <PageSeoHead title={title} description={description} path="/blog" />
      <StructuredData type="organization" />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-10 bg-charcoal text-ivory">
        <div className="container-narrow text-center">
          <span className="font-body text-xs tracking-widest uppercase text-bronze mb-2 block">
            {t('blog.label')}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold mb-3">
            {t('blog.title')}
          </h1>
          <p className="font-body text-base text-cream/80 max-w-xl mx-auto">
            {t('blog.subtitle')}
          </p>
        </div>
      </section>

      {/* Articles grid */}
      <section className="section-padding bg-background">
        <div className="container-wide">
          {isLoading || isError ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-lg overflow-hidden bg-card">
                  <Skeleton className="aspect-[16/9] w-full" />
                  <div className="p-5 space-y-3">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-5 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles && articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {articles.map((article, i) => (
                <ArticleCard key={article.id} article={article} index={i} />
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-12">
              {lang === 'it' ? 'Nessun articolo trovato.' : 'No articles found.'}
            </p>
          )}
        </div>
      </section>
    </Layout>
  );
}
